import { zodResolver } from "@hookform/resolvers/zod";
import { useAtom } from "jotai";
import { useEffect } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { financeState } from "web_myplann/Finances/State/FinanceAtom";
import { MyPlannCzechResourcesDictionary } from "web_myplann/Infrastructure/Translations/CzechResourcesDictionary";
import { MyPlannEnglishResourcesDictionary } from "web_myplann/Infrastructure/Translations/EnglishResourcesDictionaty";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { z } from "zod";

const getExactKey = (t: TranslateFunction) => {
	return t(Resources.BarChart.income);
};

const INCOME_EXACT_KEYS = [
	MyPlannCzechResourcesDictionary.BarChart.income,
	MyPlannEnglishResourcesDictionary.BarChart.income,
];

const getIncomeFormSchema = (t: TranslateFunction) =>
	z.object({
		income: z.array(
			z.object({
				id: z.number().nullable(),
				name: z.string({
					required_error: t(Resources.Form.Validations.required),
				}),
				amount: z.string({
					required_error: t(Resources.Form.Validations.required),
				}),
			}),
		),
		totalValue: z.number().optional(),
		exact: z.boolean().optional(),
	});

type IncomeFormModel = z.infer<ReturnType<typeof getIncomeFormSchema>>;

export const useIncomeForm = () => {
	const { t } = useResource();

	const [finance, setFinance] = useAtom(financeState);

	const isExact =
		finance?.income?.length &&
		finance.income.length === 1 &&
		INCOME_EXACT_KEYS.includes(finance.income[0].name)
			? false
			: finance?.income?.length
				? finance.income.length > 0
				: false;

	const form = useForm<IncomeFormModel>({
		resolver: zodResolver(getIncomeFormSchema(t)),
		values: {
			income:
				finance?.income?.map((e) => ({
					id: e.id,
					name: e.name ?? "",
					amount: String(e.amount),
				})) ?? [],
			totalValue: finance?.income?.reduce((acc, curr) => acc + curr.amount, 0),
			exact: isExact,
		},
	});

	const { control, setValue, watch, handleSubmit, reset } = form;

	useEffect(() => {
		if (finance?.income) {
			reset({
				income: finance.income.map((e) => ({
					id: e.id,
					name: e.name ?? "",
					amount: e.amount.toString(),
				})),
				exact: isExact,
			});
		}
	}, [finance?.income, reset]);

	const incomes = useWatch({
		control,
		name: "income",
	});

	const exact = useWatch({
		control,
		name: "exact",
	});

	useEffect(() => {
		const sum =
			(incomes ?? []).reduce((acc, curr) => {
				const n = Number.parseFloat(
					String(curr?.amount ?? "0").replace(",", "."),
				);
				return acc + (Number.isNaN(n) ? 0 : n);
			}, 0) ?? 0;
		setValue("totalValue", sum);
	}, [incomes, setValue, exact]);

	const {
		fields: incomeFields,
		append: incomeAppend,
		remove: incomeRemove,
	} = useFieldArray({
		control,
		name: "income",
	});

	const onSubmit = handleSubmit((data) => {
		if (!exact) {
			const wasMultiple = (finance?.income?.length ?? 0) > 1;
			setFinance({
				income: [
					{
						id: wasMultiple ? null : (finance?.income?.[0]?.id ?? null),
						name: getExactKey(t),
						amount: data.totalValue ?? 0,
					},
				],
				expenses: finance?.expenses ?? [],
			});
			return;
		}
		setFinance({
			income: data.income.map((e) => ({
				id: e.id,
				name: e.name,
				amount: Number(e.amount),
			})),
			expenses: finance?.expenses ?? [],
		});
	});

	return {
		incomeFields,
		incomeAppend,
		incomeRemove,
		setValue,
		watch,
		onSubmit,
		control,
	};
};
